import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { industriesAPI } from '../lib/api';
import type { Industry } from '../types/leads';

export function useIndustries(organizationId: string | undefined) {
  return useQuery<Industry[]>({
    queryKey: ['industries', organizationId],
    queryFn: async () => {
      const { data } = await industriesAPI.getAll(organizationId!);
      return data;
    },
    enabled: !!organizationId,
  });
}

export function useCreateIndustry() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (payload: { name: string }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data } = await industriesAPI.create(orgId, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industries', currentOrganization?.id] });
    },
  });
}

export function useDeleteIndustry() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      await industriesAPI.remove(orgId, id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['industries', currentOrganization?.id] });
    },
  });
}
